import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, message, Typography, Spin, Empty, Row, Col, Segmented } from 'antd';
import { DownloadOutlined, ToolOutlined, CodeOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../../services/api';
import './index.css';

const { Title, Text, Paragraph } = Typography;

interface SharedComponent {
  id: string; uuid: string; slug: string; name: string; description: string;
  version: string; type: string; category: string; size: string; author: string; tags?: string[];
}

const TYPE_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  development: { color: 'blue', icon: <CodeOutlined />, label: '开发组件' },
  tool: { color: 'green', icon: <ToolOutlined />, label: '工具组件' },
};

export default function TechComponentsPage() {
  const [components, setComponents] = useState<SharedComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState<string | null>(null);
  const [flyTarget, setFlyTarget] = useState<{x:number;y:number;id:string;name:string} | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => { loadComponents(); }, [filterType]);

  useEffect(() => { if (flyTarget) { const t = setTimeout(() => setFlyTarget(null), 2000); return () => clearTimeout(t); } }, [flyTarget]);

  const loadComponents = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterType !== 'all') params.type = filterType;
      const { data } = await api.get('/tech-components/shared', { params });
      setComponents(data || []);
    } catch {}
    setLoading(false);
  };

  const handleInstall = async (comp: SharedComponent, btnEl?: HTMLElement) => {
    setInstalling(comp.id);
    try {
      const wbRes = await api.get('/dev-workspace/workbenches');
      const wbs = wbRes.data || [];
      if (wbs.length === 0) { message.warning('请先在AI全流程中创建工作台'); return; }
      await api.post('/tech-components/install', {
        componentId: comp.slug || comp.id,
        componentUuid: comp.uuid,
        workbenchId: wbs[0].id,
      });
      message.success(`${comp.name} 已安装到组件库`);
      if (btnEl) {
        const rect = btnEl.getBoundingClientRect();
        setFlyTarget({ x: rect.left + rect.width/2, y: rect.top, id: comp.id, name: comp.name });
      }
    } catch (e: any) { message.error(e.response?.data?.message || '安装失败'); }
    setInstalling(null);
  };

  const copyUuid = (uuid: string, name: string) => {
    const text = `@组件[${name}]#${uuid}`;
    navigator.clipboard.writeText(text).then(() => message.success('组件引用已复制到剪贴板')).catch(() => message.info('引用格式: ' + text));
  };

  const filtered = components;

  return (
    <div className="tech-components-page" style={{ padding: '24px 32px', minHeight: '100vh', background: '#f5f5f5' }}>
      <div className="tech-components-hero" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}><ToolOutlined style={{ marginRight: 8, color: '#1677ff' }} />技术组件</Title>
          <Text type="secondary">可复用的技术组件库 — 安装后可在AI全流程和团队协作中引用</Text>
        </div>
        <Segmented
          options={[
            { label: '全部', value: 'all' },
            { label: '开发组件', value: 'development', icon: <CodeOutlined /> },
            { label: '工具组件', value: 'tool', icon: <ToolOutlined /> },
          ]}
          value={filterType}
          onChange={v => setFilterType(v as string)}
        />
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      : filtered.length === 0 ? <Empty description="暂无共享组件" />
      : (
        <Row gutter={[16, 16]}>
          {filtered.map(comp => {
            const tc = TYPE_CONFIG[comp.type] || { color: 'default', icon: null, label: comp.type };
            return (
              <Col key={comp.id} xs={24} sm={12} lg={8}>
                <Card hoverable className="tech-component-card" style={{ borderRadius: 12 }} actions={[
                  <Button key="copy" type="link" icon={<CopyOutlined />} size="small"
                    onClick={() => copyUuid(comp.uuid, comp.name)}
                    style={{ color: '#666' }}>复制引用</Button>,
                  <Button key="install" type="link" icon={<DownloadOutlined />} loading={installing === comp.id}
                    onClick={(e) => handleInstall(comp, (e.target as HTMLElement).closest('button') || undefined)}
                    style={{ color: '#1677ff' }}>安装</Button>,
                ]}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Tag color={tc.color}>{tc.label}</Tag>
                    <Tag>{comp.category}</Tag>
                    {comp.size && <Tag>{comp.size}</Tag>}
                  </div>
                  <Title level={5} style={{ margin: '8px 0 4px' }}>
                    {tc.icon} {comp.name}
                  </Title>
                  <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 4 }} ellipsis={{ rows: 2 }}>
                    {comp.description}
                  </Paragraph>
                  {comp.uuid && (
                    <Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>
                      UUID: {comp.uuid.slice(0, 8)}...
                    </Text>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {flyTarget && (
        <div style={{
          position: 'fixed', left: flyTarget.x, top: flyTarget.y,
          zIndex: 9999, pointerEvents: 'none',
          animation: 'flyToHeader 1.5s ease-out forwards',
          background: 'linear-gradient(135deg, #1677ff, #69b1ff)',
          color: '#fff', borderRadius: 20, padding: '4px 12px',
          fontSize: 12, fontWeight: 600, boxShadow: '0 4px 16px rgba(22,119,255,0.4)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          🧩 {flyTarget.name} ✓
        </div>
      )}
      <style>{`@keyframes flyToHeader {
        0% { opacity:1; transform:translate(0,0) scale(1); }
        30% { opacity:1; transform:translate(calc(50vw - ${flyTarget?.x || 0}px), -60px) scale(1.3); }
        100% { opacity:0; transform:translate(calc(80vw - ${flyTarget?.x || 0}px), -80px) scale(0.5); }
      }`}</style>
    </div>
  );
}
